import type { ZoneLength, ZonesConfig } from '../config/zones.config';
import { Random } from '../utils/Random';

/** Lighting zone of a stretch of track. */
export const LightingZone = {
  Day: 'DAY',
  Night: 'NIGHT',
} as const;
export type LightingZone = (typeof LightingZone)[keyof typeof LightingZone];

/** One zone: [from, to) in track meters. */
export interface ZoneSpan {
  readonly zone: LightingZone;
  readonly from: number;
  readonly to: number;
}

/**
 * Day/night zones along the run (pure, deterministic per seed): the run starts with a day
 * zone, then night and day alternate with random lengths from the config ranges. Spans are
 * generated lazily as far as they are asked for. Purely cosmetic – it never touches the
 * gameplay random streams (the caller derives an own seed).
 */
export class ZoneSchedule {
  private readonly spans: ZoneSpan[] = [];
  private readonly rng: Random;

  constructor(
    seed: number,
    private readonly schedule: ZonesConfig['schedule'],
  ) {
    this.rng = new Random(seed);
    this.spans.push({ zone: LightingZone.Day, from: 0, to: this.length(schedule.firstDay) });
  }

  zoneAt(distance: number): LightingZone {
    return this.spanAt(distance).zone;
  }

  isNight(distance: number): boolean {
    return this.zoneAt(distance) === LightingZone.Night;
  }

  /** The zone containing `distance` (distances before the start belong to the first zone). */
  spanAt(distance: number): ZoneSpan {
    let last = this.spans[this.spans.length - 1] as ZoneSpan;
    while (last.to <= distance) {
      const zone = last.zone === LightingZone.Day ? LightingZone.Night : LightingZone.Day;
      last = { zone, from: last.to, to: last.to + this.length(zone === LightingZone.Day ? this.schedule.day : this.schedule.night) };
      this.spans.push(last);
    }
    // Binary search: spans are contiguous and sorted.
    let lo = 0;
    let hi = this.spans.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((this.spans[mid] as ZoneSpan).to <= distance) lo = mid + 1;
      else hi = mid;
    }
    return this.spans[lo] as ZoneSpan;
  }

  private length(range: ZoneLength): number {
    return this.rng.range(range.min, range.max);
  }
}

/** Random.deriveSeed stream of the zone schedule – separate from the track streams (1–3), cosmetic only. */
export const ZONE_SEED_STREAM = 9;
